/**
 * codiceFiscale.js — il codice fiscale e gli altri documenti del paziente.
 *
 * Due livelli:
 *  1. formato + carattere di controllo (checksum) — intercetta i typo
 *  2. coerenza con data di nascita e sesso gia' inseriti — intercetta
 *     il CF valido ma abbinato ai dati sbagliati
 *
 * IL LUOGO DI NASCITA (3/10/2026, ramo cf-estero). Le posizioni 12-15 sono
 * il codice catastale del luogo: un comune italiano (H294, Rimini), uno
 * Stato estero (la Z: Z404, Stati Uniti d'America) o un comune italiano
 * ceduto nel 1947 (G778, Pola), che per chi ci e' nato prima tiene il suo
 * codice italiano e non la Z dello Stato di oggi. `luogoDalCF` legge il
 * codice e basta: il nome lo dice il database (comuni, stati_esteri,
 * comuni_ceduti - migration 287), perche' qui dentro non si porta una
 * tabella di ottomila righe.
 *
 * Le tre cifre del codice del luogo sono le PRIME che l'omocodia
 * sostituisce con una lettera (si parte da destra): Z4LQ e' Z404. Fino al
 * 3/10/2026 il codice del luogo si leggeva senza tradurle, e un codice
 * omocodico risultava nato in un comune «soppresso o accorpato».
 *
 * STP ED ENI. Il codice STP (straniero temporaneamente presente) e l'ENI
 * (europeo non iscritto) hanno sedici caratteri come un codice fiscale ma
 * non lo sono: tre lettere e tredici cifre (regione e ASL che lo
 * rilasciano, poi un progressivo). Da loro non si calcola niente. Decide
 * la FORMA intera, non il prefisso: STP sono anche le prime tre consonanti
 * di un cognome, e in archivio ci sono codici fiscali veri che cominciano
 * cosi' (sei, contati il 3/10/2026).
 *
 * DUE COPIE, UNA PROVA. Questo file vive in backend/utils e in
 * frontend/src/utils (il browser non importa dal backend), uguale salvo le
 * righe delle esportazioni. frontend/scripts/verificaCodiceFiscale.js li
 * carica tutti e due e confronta le risposte su qualche migliaio di codici:
 * e' nel prebuild, e se divergono la build non parte. Chi cambia una copia
 * cambia l'altra.
 */

const PARI = {
    0: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9,
    A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6, H: 7, I: 8, J: 9,
    K: 10, L: 11, M: 12, N: 13, O: 14, P: 15, Q: 16, R: 17, S: 18,
    T: 19, U: 20, V: 21, W: 22, X: 23, Y: 24, Z: 25
};

const DISPARI = {
    0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21,
    A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21,
    K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14,
    U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23
};

const RESTO = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Mese: lettera -> numero (0-based)
const MESI = { A: 0, B: 1, C: 2, D: 3, E: 4, H: 5, L: 6, M: 7, P: 8, R: 9, S: 10, T: 11 };

/* Le posizioni numeriche possono essere lettere (OMOCODIA: quando due
   persone avrebbero lo stesso codice, l'Agenzia sostituisce cifre con
   L M N P Q R S T U V, a partire da destra). Prima il formato le
   rifiutava, e un codice vero risultava «non valido». */
const CIFRA = '[0-9LMNPQRSTUV]';
const FORMATO = new RegExp(`^[A-Z]{6}${CIFRA}{2}[A-Z]${CIFRA}{2}[A-Z]${CIFRA}{3}[A-Z]$`);
const OMOCODIA = { L: '0', M: '1', N: '2', P: '3', Q: '4', R: '5', S: '6', T: '7', U: '8', V: '9' };
const cifre = (s) => s.replace(/[LMNPQRSTUV]/g, (c) => OMOCODIA[c]);

/* STP o ENI, poi tredici cifre: tre della regione, tre dell'ASL, sette
   del progressivo. Solo la forma: chi rilascia il codice non si verifica. */
const FORMATO_STP_ENI = /^(STP|ENI)\d{13}$/;

/* I tipi di documento del paziente: lo stesso elenco del CHECK su
   pazienti_tab e anagrafiche_da_confermare (migration 287). Un elenco di
   stati vive in due posti, e vanno cambiati insieme (CLAUDE.md). */
const TIPI_DOCUMENTO = ['cf_italiano', 'iss_san_marino', 'estero', 'stp_eni'];

const normalizza = (codice) => String(codice || '').toUpperCase().replace(/\s/g, '');

/**
 * Carattere di controllo atteso per le prime 15 posizioni.
 */
function carattereControllo(cf15) {
    let somma = 0;
    for (let i = 0; i < 15; i++) {
        const c = cf15[i];
        somma += (i % 2 === 0) ? DISPARI[c] : PARI[c];
    }
    return RESTO[somma % 26];
}

/**
 * La forma di un codice, senza giudicarlo: 'cf' (la forma di un codice
 * fiscale, carattere di controllo escluso), 'stp', 'eni' o 'altro'.
 */
function formaCodice(codice) {
    const c = normalizza(codice);
    if (FORMATO.test(c)) return 'cf';
    const m = FORMATO_STP_ENI.exec(c);
    if (m) return m[1].toLowerCase();
    return 'altro';
}

/**
 * Estrae giorno, mese, anno (2 cifre) e sesso dal CF.
 * Nel CF il giorno della donna e' aumentato di 40.
 */
function datiDalCF(cf) {
    const anno = parseInt(cifre(cf.substring(6, 8)), 10);
    const mese = MESI[cf[8]];
    let giorno = parseInt(cifre(cf.substring(9, 11)), 10);
    const sesso = giorno > 40 ? 'F' : 'M';
    if (giorno > 40) giorno -= 40;
    return { anno, mese, giorno, sesso };
}

/**
 * Valida il codice fiscale.
 *
 * @param {string} cf
 * @param {object} [anagrafica]  { data_nascita: 'YYYY-MM-DD', sesso: 'M'|'F' }
 * @returns {{ valido: boolean, errore: string|null, avvisi: string[] }}
 */
function validaCodiceFiscale(cf, anagrafica = {}) {
    const avvisi = [];
    const codice = normalizza(cf);

    if (!codice) return { valido: false, errore: 'Codice fiscale mancante', avvisi };
    if (codice.length !== 16) return { valido: false, errore: 'Il codice fiscale deve essere di 16 caratteri', avvisi };

    /* Sedici caratteri che non sono un codice fiscale: lo si dice col suo
       nome, invece di «formato non valido», che manda a cercare un errore
       di battitura che non c'e'. */
    const stpEni = FORMATO_STP_ENI.exec(codice);
    if (stpEni) {
        return {
            valido: false,
            errore: `È un codice ${stpEni[1]} e non un codice fiscale: va indicato come «Codice STP o ENI».`,
            avvisi,
        };
    }

    if (!FORMATO.test(codice)) return { valido: false, errore: 'Formato del codice fiscale non valido', avvisi };

    // 1. Carattere di controllo
    const atteso = carattereControllo(codice.substring(0, 15));
    if (atteso !== codice[15]) {
        return { valido: false, errore: 'Codice fiscale non valido: carattere di controllo errato', avvisi };
    }

    // 2. Coerenza con i dati anagrafici, se disponibili
    const dati = datiDalCF(codice);

    if (dati.mese === undefined) {
        return { valido: false, errore: 'Codice fiscale non valido: mese di nascita non riconosciuto', avvisi };
    }

    /* LA COERENZA CON DATA E SESSO E' UN AVVISO, NON UN BLOCCO (Alex,
       26/09/2026): il codice puo' avere un'omocodia o un errore
       dell'Agenzia, e la persona ha la data che ha. Si segnala in modo
       chiaro e si lascia andare avanti. */
    if (anagrafica.sesso && (anagrafica.sesso === 'M' || anagrafica.sesso === 'F')) {
        if (anagrafica.sesso !== dati.sesso) {
            avvisi.push(`Il codice fiscale indica sesso ${dati.sesso}, ma è stato indicato ${anagrafica.sesso}: controlla.`);
        }
    }

    if (anagrafica.data_nascita) {
        const d = new Date(anagrafica.data_nascita);
        if (!isNaN(d)) {
            const giornoOK = d.getDate() === dati.giorno;
            const meseOK = d.getMonth() === dati.mese;
            const annoOK = (d.getFullYear() % 100) === dati.anno;

            if (!giornoOK || !meseOK || !annoOK) {
                avvisi.push('La data di nascita non corrisponde al codice fiscale: controlla quale dei due è giusto.');
            }
        }
    }

    return { valido: true, errore: null, avvisi };
}


/**
 * Data di nascita e sesso letti da un codice fiscale italiano VALIDO, per
 * precompilarli (26/09/2026). Il secolo non sta nel codice: si prende il
 * piu' recente che non cada nel futuro, e il paziente conferma.
 * Restituisce null se il codice non e' valido.
 */
function nascitaDalCF(cf, oggi = new Date()) {
    const codice = normalizza(cf);
    if (codice.length !== 16 || !FORMATO.test(codice)) return null;
    if (carattereControllo(codice.substring(0, 15)) !== codice[15]) return null;
    const d = datiDalCF(codice);
    if (d.mese === undefined || d.giorno < 1 || d.giorno > 31) return null;
    let anno = 2000 + d.anno;
    const data = (a) => new Date(Date.UTC(a, d.mese, d.giorno));
    if (data(anno) > oggi) anno -= 100;
    const x = data(anno);
    if (x.getUTCMonth() !== d.mese || x.getUTCDate() !== d.giorno) return null;   // 31 febbraio
    const iso = `${anno}-${String(d.mese + 1).padStart(2, '0')}-${String(d.giorno).padStart(2, '0')}`;
    return { data_nascita: iso, sesso: d.sesso };
}

/**
 * Il codice catastale del luogo di nascita letto da un codice fiscale
 * VALIDO (formato e carattere di controllo), con l'omocodia tradotta:
 * { codice: 'Z404', estero: true } oppure { codice: 'H294', estero: false }.
 * Restituisce null se il codice non e' un codice fiscale valido: da un
 * codice sbagliato non si propone un luogo.
 *
 * `estero` dice solo che il codice comincia per Z. Un comune ceduto nel
 * 1947 ha un codice italiano, quindi qui risulta `estero: false`: quale dei
 * tre casi sia lo dice il database (rotta /api/comuni/luogo).
 */
function luogoDalCF(cf) {
    const codice = normalizza(cf);
    if (codice.length !== 16 || !FORMATO.test(codice)) return null;
    if (carattereControllo(codice.substring(0, 15)) !== codice[15]) return null;
    const luogo = codice[11] + cifre(codice.substring(12, 15));
    return { codice: luogo, estero: luogo[0] === 'Z' };
}

/**
 * Valida il documento identificativo secondo il tipo.
 *
 * @param {string} codice
 * @param {string} tipo        cf_italiano | iss_san_marino | estero | stp_eni
 * @param {object} anagrafica  { data_nascita, sesso }
 */
function validaDocumento(codice, tipo = 'cf_italiano', anagrafica = {}) {
    const c = normalizza(codice);

    if (tipo === 'iss_san_marino') {
        if (!c) return { valido: false, errore: 'Codice ISS mancante', avvisi: [] };
        if (!/^\d{1,7}$/.test(c)) {
            return {
                valido: false,
                errore: 'Il codice ISS di San Marino è composto da un massimo di sette cifre',
                avvisi: []
            };
        }
        return {
            valido: true,
            errore: null,
            avvisi: ['Le prestazioni di questo paziente non vanno trasmesse al Sistema TS.']
        };
    }

    if (tipo === 'estero') {
        if (!c) return { valido: false, errore: 'Documento identificativo mancante', avvisi: [] };
        return {
            valido: true,
            errore: null,
            avvisi: ['Le prestazioni di questo paziente non vanno trasmesse al Sistema TS.']
        };
    }

    /* STP ED ENI (3/10/2026): un controllo di FORMA e basta. Chi lo ha
       rilasciato (regione, ASL) non si verifica, e da questo codice non si
       ricavano ne' la data ne' il luogo di nascita. */
    if (tipo === 'stp_eni') {
        if (!c) return { valido: false, errore: 'Codice STP o ENI mancante', avvisi: [] };
        if (!FORMATO_STP_ENI.test(c)) {
            return {
                valido: false,
                errore: 'Il codice STP o ENI è fatto di tre lettere (STP o ENI) e tredici cifre',
                avvisi: []
            };
        }
        return {
            valido: true,
            errore: null,
            avvisi: ['Non è un codice fiscale: data e luogo di nascita non si ricavano dal codice.']
        };
    }

    return validaCodiceFiscale(c, anagrafica);
}

/**
 * Codice catastale del comune di nascita: caratteri 12-15, con
 * l'omocodia tradotta.
 *
 * Restituisce null per chi e' nato all'estero (codice che inizia per
 * Z), che non e' nella tabella dei comuni, e per un codice che non ha la
 * forma di un codice fiscale.
 */
function codiceCatastaleDaCF(cf) {
    const c = normalizza(cf);
    if (c.length !== 16 || !FORMATO.test(c)) return null;
    const cat = c[11] + cifre(c.substring(12, 15));
    if (cat.startsWith('Z')) return null;
    return cat;
}

export { validaCodiceFiscale, carattereControllo, validaDocumento, codiceCatastaleDaCF, nascitaDalCF,
         luogoDalCF, formaCodice, TIPI_DOCUMENTO };
export default validaCodiceFiscale;
